import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CGV() {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';
    const scriptId = 'jsonld-cgv';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Conditions Générales de Vente | Usine à Leads',
      description: "Conditions générales de vente d'Usine à Leads : tarifs, paiement, obligations, résiliation et limitation de responsabilité pour les prestations de génération de leads.",
      url: `${siteUrl}/cgv`,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: 'Usine à Leads',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/62fb907743b5e07417bde9cd3a46f134.png',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'CGV', item: `${siteUrl}/cgv` },
        ],
      },
    });
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <title>Conditions Générales de Vente | Usine à Leads</title>
      <meta name="description" content="Consultez les conditions générales de vente d'Usine à Leads : tarifs, paiement, durée, résiliation et limitation de responsabilité pour nos prestations de génération de leads." />
      <meta name="keywords" content="CGV, conditions générales vente, prestations génération leads, usine à leads" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/cgv`} />
      <meta property="og:title" content="Conditions Générales de Vente | Usine à Leads" />
      <meta property="og:description" content="Conditions générales de vente d'Usine à Leads : tarifs, paiement, obligations et résiliation pour les prestations de génération de leads." />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/cgv`} />
      <meta property="og:type" content="website" />

      {/* Navbar */}
      <nav className="w-full bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/8eef813465f798e5a4134d50e400907e.png"
              alt="Usine à Leads"
              className="h-10 w-auto"
              loading="eager"
              decoding="async"
              width="140"
              height="40"
            />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Retour à l&apos;accueil
          </Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
          Conditions Générales de Vente
        </h1>
        <p className="text-sm text-gray-400 mb-10">
          Dernière mise à jour :{' '}
          {new Date().toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
        <hr className="border-gray-100 mb-10" />

        <article className="space-y-10 text-gray-700 text-[15px] leading-relaxed">
          {/* Préambule */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Préambule</h2>
            <p>
              Les présentes Conditions Générales de Vente (ci-après « CGV ») régissent les relations
              contractuelles entre <strong>Usine à Leads</strong> (ci-après « le Prestataire ») et toute
              personne physique ou morale (ci-après « le Client ») souhaitant bénéficier des services
              proposés par le Prestataire.
            </p>
            <p className="mt-3">
              Toute commande de services implique l&apos;acceptation sans réserve des présentes CGV par le
              Client.
            </p>
          </section>

          {/* Objet */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Objet</h2>
            <p>
              Les présentes CGV ont pour objet de définir les conditions dans lesquelles le Prestataire
              fournit au Client des <strong>prestations de services logicielles, d&apos;agence et de média</strong>,
              incluant notamment :
            </p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  La mise en place de stratégies d&apos;acquisition digitale (publicité en ligne, SEO,
                  réseaux sociaux)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  La création et l&apos;optimisation de tunnels de conversion et de pages de capture
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  La mise en place d&apos;outils d&apos;automatisation marketing (CRM, emailing,
                  relances)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Le conseil et l&apos;accompagnement en stratégie de génération de leads</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>La gestion de campagnes publicitaires sur les plateformes média</span>
              </li>
            </ul>
          </section>

          {/* Obligation de moyens */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Obligation de moyens</h2>
            <div className="bg-orange-50 rounded-xl p-5 border-l-4 border-orange-400">
              <p className="font-semibold text-gray-900 mb-2">
                <i className="ri-error-warning-line text-orange-500 mr-2"></i>
                Clause essentielle
              </p>
              <p>
                Le Prestataire est soumis à une <strong>obligation de moyens</strong> et non de résultats.
                Le Prestataire s&apos;engage à mettre en œuvre tous les moyens nécessaires et son savoir-faire
                pour la bonne exécution des prestations convenues, sans toutefois pouvoir garantir
                l&apos;atteinte de résultats spécifiques.
              </p>
              <p className="mt-3">
                Les performances des campagnes publicitaires, le volume de leads générés, le taux de
                conversion et tout autre indicateur de performance dépendent de nombreux facteurs
                extérieurs au Prestataire (marché, concurrence, saisonnalité, qualité de l&apos;offre du
                Client, etc.) et ne sauraient faire l&apos;objet d&apos;un engagement de résultats.
              </p>
            </div>
          </section>

          {/* Tarifs et paiement */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">4. Tarifs et modalités de paiement</h2>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.1 Tarifs</h3>
            <p>
              Les tarifs des prestations sont indiqués en euros et hors taxes (HT), sauf mention contraire.
              La TVA applicable sera ajoutée au taux en vigueur. Les tarifs sont ceux en vigueur au jour
              de la commande et sont détaillés dans le devis ou la proposition commerciale acceptée par le
              Client.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.2 Budgets média</h3>
            <p>
              Les budgets publicitaires (Facebook Ads, Google Ads, LinkedIn Ads, etc.) sont à la charge
              exclusive du Client et ne sont pas inclus dans les honoraires du Prestataire. Ces budgets
              sont versés directement aux plateformes publicitaires par le Client.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.3 Modalités de paiement</h3>
            <p>Le paiement s&apos;effectue selon les modalités définies dans le devis ou la proposition commerciale :</p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Paiement par virement bancaire ou prélèvement automatique</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Facturation mensuelle ou selon l&apos;échéancier convenu</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Délai de paiement : 30 jours à compter de la date de facturation, sauf accord contraire</span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">4.4 Retard de paiement</h3>
            <p>
              En cas de retard de paiement, des pénalités de retard seront appliquées au taux de trois
              fois le taux d&apos;intérêt légal en vigueur, conformément à l&apos;article L.441-10 du Code de
              commerce. Une indemnité forfaitaire de 40 € pour frais de recouvrement sera également due
              (article D.441-5 du Code de commerce).
            </p>
            <p className="mt-3">
              Le Prestataire se réserve le droit de suspendre l&apos;exécution des prestations en cas de
              non-paiement dans les délais convenus.
            </p>
          </section>

          {/* Durée et résiliation */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Durée et résiliation</h2>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">5.1 Durée</h3>
            <p>
              La durée des prestations est définie dans le devis ou la proposition commerciale.
              Les contrats peuvent être à durée déterminée ou indéterminée selon les prestations choisies.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">5.2 Résiliation</h3>
            <p>Chaque partie peut résilier le contrat dans les conditions suivantes :</p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Contrat à durée indéterminée :</strong> résiliation à tout moment avec un préavis de 30
                  jours par lettre recommandée avec accusé de réception ou par email avec confirmation
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Contrat à durée déterminée :</strong> le contrat prend fin à son terme. Toute résiliation
                  anticipée devra respecter les conditions prévues au devis
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  <strong>Résiliation pour faute :</strong> en cas de manquement grave d&apos;une partie à ses obligations,
                  l&apos;autre partie pourra résilier le contrat de plein droit après mise en demeure restée
                  infructueuse pendant 15 jours
                </span>
              </li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mt-6 mb-3">5.3 Conséquences de la résiliation</h3>
            <p>
              En cas de résiliation, les prestations réalisées jusqu&apos;à la date effective de résiliation
              restent dues. Le Client conserve la propriété des éléments créés et livrés dans le cadre
              des prestations payées.
            </p>
          </section>

          {/* Obligations du Client */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Obligations du Client</h2>
            <p>Le Client s&apos;engage à :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  Fournir au Prestataire toutes les informations et éléments nécessaires à la bonne exécution des
                  prestations dans les délais convenus
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Collaborer activement avec le Prestataire et répondre aux sollicitations dans un délai raisonnable</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Donner les accès nécessaires aux outils et plateformes requis pour l&apos;exécution des prestations</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Valider les livrables dans les délais convenus (à défaut, les livrables seront réputés acceptés)</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>S&apos;assurer de la conformité de son offre commerciale avec la réglementation en vigueur</span>
              </li>
            </ul>
          </section>

          {/* Obligations du Prestataire */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Obligations du Prestataire</h2>
            <p>Le Prestataire s&apos;engage à :</p>
            <ul className="mt-4 space-y-3">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Exécuter les prestations avec diligence et professionnalisme, conformément aux règles de l&apos;art</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Informer régulièrement le Client de l&apos;avancement des prestations</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Respecter la confidentialité des informations communiquées par le Client</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Mettre en œuvre les moyens nécessaires à la bonne exécution des prestations</span>
              </li>
            </ul>
          </section>

          {/* Propriété intellectuelle */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Propriété intellectuelle</h2>
            <p>
              Les livrables créés dans le cadre des prestations (pages web, visuels, textes, tunnels de
              conversion, etc.) sont la propriété du Client après paiement intégral des sommes dues.
            </p>
            <p className="mt-3">
              Les outils, méthodes, logiciels et savoir-faire développés par le Prestataire antérieurement
              ou indépendamment des prestations restent la propriété exclusive du Prestataire.
            </p>
            <p className="mt-3">
              Le Prestataire se réserve le droit de mentionner les prestations réalisées pour le Client
              à titre de référence commerciale, sauf opposition écrite du Client.
            </p>
          </section>

          {/* Confidentialité */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Confidentialité</h2>
            <p>
              Chaque partie s&apos;engage à garder confidentielles toutes les informations et documents
              communiqués par l&apos;autre partie dans le cadre de l&apos;exécution du contrat. Cette obligation
              de confidentialité perdure pendant toute la durée du contrat et pendant une durée de 2 ans
              après son terme.
            </p>
          </section>

          {/* Responsabilité */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Limitation de responsabilité</h2>
            <p>
              La responsabilité du Prestataire est limitée aux dommages directs et prévisibles résultant
              d&apos;un manquement prouvé à ses obligations contractuelles.
            </p>
            <p className="mt-3">
              En tout état de cause, la responsabilité totale du Prestataire ne pourra excéder le montant
              total des sommes effectivement perçues au titre du contrat au cours des 12 derniers mois
              précédant le fait générateur.
            </p>
            <p className="mt-3">Le Prestataire ne saurait être tenu responsable :</p>
            <ul className="mt-3 space-y-2">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  Des résultats commerciaux obtenus par le Client (nombre de leads, taux de conversion,
                  chiffre d&apos;affaires)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  Des modifications apportées par les plateformes publicitaires (changements d&apos;algorithme,
                  politiques publicitaires)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>
                  Des dommages indirects tels que perte de chiffre d&apos;affaires, perte de clientèle,
                  perte de données
                </span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-orange-500 mt-0.5 flex-shrink-0"></i>
                <span>Du non-respect par le Client de ses propres obligations</span>
              </li>
            </ul>
          </section>

          {/* Force majeure */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Force majeure</h2>
            <p>
              Aucune des parties ne pourra être tenue responsable de l&apos;inexécution de ses obligations
              en cas de force majeure telle que définie par l&apos;article 1218 du Code civil. En cas de
              force majeure d&apos;une durée supérieure à 3 mois, chaque partie pourra résilier le contrat
              sans indemnité.
            </p>
          </section>

          {/* Données personnelles */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">12. Protection des données personnelles</h2>
            <p>
              Le Prestataire s&apos;engage à respecter la réglementation en vigueur relative à la protection
              des données personnelles, notamment le RGPD. Pour plus d&apos;informations, consultez notre{' '}
              <Link
                to="/politique-de-confidentialite"
                className="text-orange-500 hover:underline cursor-pointer"
              >
                Politique de confidentialité
              </Link>
              .
            </p>
          </section>

          {/* Droit de rétractation */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">13. Droit de rétractation</h2>
            <p>
              Conformément à l&apos;article L.221-28 du Code de la consommation, le droit de rétractation
              ne s&apos;applique pas aux contrats de fourniture de services pleinement exécutés avant la fin
              du délai de rétractation et dont l&apos;exécution a commencé après accord préalable exprès du
              consommateur et renoncement exprès à son droit de rétractation.
            </p>
            <p className="mt-3">
              Pour les prestations n&apos;ayant pas encore débuté, le Client consommateur dispose d&apos;un
              délai de 14 jours à compter de la conclusion du contrat pour exercer son droit de rétractation,
              sans avoir à justifier de motifs ni à payer de pénalités.
            </p>
          </section>

          {/* Réclamations */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">14. Réclamations et médiation</h2>
            <p>
              Toute réclamation doit être adressée au Prestataire par email à{' '}
              <a
                href="mailto:contact@usineleads.com"
                className="text-orange-500 hover:underline cursor-pointer"
              >
                contact@usineleads.com
              </a>{' '}
              ou par courrier recommandé au siège social.
            </p>
            <p className="mt-3">
              Conformément aux articles L.616-1 et R.616-1 du Code de la consommation, en cas de litige
              non résolu, le Client consommateur peut recourir gratuitement à un médiateur de la consommation.
              Le médiateur proposé est : [À compléter].
            </p>
            <p className="mt-3">
              Le Client peut également déposer sa réclamation sur la plateforme européenne de règlement
              en ligne des litiges :{' '}
              <a
                href="https://ec.europa.eu/consumers/odr"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-orange-500 hover:underline cursor-pointer"
              >
                https://ec.europa.eu/consumers/odr
              </a>
            </p>
          </section>

          {/* Droit applicable */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">15. Droit applicable et juridiction compétente</h2>
            <p>
              Les présentes CGV sont régies par le droit français. En cas de litige relatif à
              l&apos;interprétation ou à l&apos;exécution des présentes, et après tentative de résolution amiable,
              les tribunaux compétents de Paris seront seuls compétents.
            </p>
            <p className="mt-3">
              Si une ou plusieurs stipulations des présentes CGV sont tenues pour non valides ou déclarées
              comme telles en application d&apos;une loi, d&apos;un règlement ou à la suite d&apos;une décision
              définitive d&apos;une juridiction compétente, les autres stipulations garderont toute leur force
              et leur portée.
            </p>
          </section>
        </article>
      </main>

      {/* Footer minimal */}
      <footer className="border-t border-gray-100 py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-400">
          <p>© {new Date().getFullYear()} Usine à Leads. Tous droits réservés.</p>
          <nav className="flex gap-6">
            <Link
              to="/mentions-legales"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Mentions légales
            </Link>
            <Link
              to="/politique-de-confidentialite"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Politique de confidentialité
            </Link>
            <Link
              to="/cgv"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap font-medium text-gray-600"
            >
              CGV
            </Link>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-orange-500 transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
